import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";

import { useIsMobile } from "@src/lib/hooks/useIsMobile";
import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import { Input } from "@src/lib/ui/Input";
import { Separator } from "@src/lib/ui/Separator";
import { Sheet, SheetContent } from "@src/lib/ui/Sheet";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@src/lib/ui/Tooltip";
import SidebarLeftOutlineIcon from "@src/lib/icons/18px/sidebar-left-outline";
import { textV4Variants } from "@src/lib/ui/Text";
import CaretDownFillIcon from "@src/lib/icons/18px/caret-down-fill";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "240px";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "48px";
const ADMIN_PRIMARY_RAIL_COLLAPSED_WIDTH = "56px";
const ADMIN_PRIMARY_RAIL_EXPANDED_WIDTH = "200px";
const ADMIN_SECONDARY_MENU_WIDTH = "220px";
const ADMIN_PRIMARY_RAIL_PINNED_STORAGE_KEY = "admin-primary-rail:pinned";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContext = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  primaryRailPinned: boolean;
  setPrimaryRailPinned: (pinned: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  // A mobile Sheet registers itself while rendered; returns the cleanup.
  registerMobileSheet: () => () => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a Sidebar.");
  }

  return context;
}

const SidebarProvider = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      style,
      children,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);
    // Below md only some navs render the off-canvas Sheet (the admin nav is
    // a rail at every width), so the shortcut toggles the Sheet only when
    // one is mounted and the visible nav's open state otherwise.
    const mobileSheetCount = React.useRef(0);
    const registerMobileSheet = React.useCallback(() => {
      mobileSheetCount.current += 1;
      return () => {
        mobileSheetCount.current -= 1;
      };
    }, []);
    const [primaryRailPinned, setPrimaryRailPinnedState] =
      React.useState(false);

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        if (setOpenProp) {
          return setOpenProp?.(
            typeof value === "function" ? value(open) : value,
          );
        }

        _setOpen(value);

        // This sets the cookie to keep the sidebar state.
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [setOpenProp, open],
    );
    const setPrimaryRailPinned = React.useCallback((pinned: boolean) => {
      setPrimaryRailPinnedState(pinned);
      window.localStorage.setItem(
        ADMIN_PRIMARY_RAIL_PINNED_STORAGE_KEY,
        pinned.toString(),
      );
    }, []);

    React.useEffect(() => {
      setPrimaryRailPinnedState(
        window.localStorage.getItem(ADMIN_PRIMARY_RAIL_PINNED_STORAGE_KEY) ===
          "true",
      );
    }, []);

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
      return isMobile && mobileSheetCount.current > 0
        ? setOpenMobile((open) => !open)
        : setOpen((open) => !open);
    }, [isMobile, setOpen, setOpenMobile]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey)
        ) {
          // Check if user is typing in a text input or editor
          if (event.target instanceof Element) {
            if (event.target.matches("input")) return;
            if (event.target.matches("textarea")) return;
            if (event.target.classList.contains("ProseMirror")) return;
            if (event.target.closest("[contenteditable]")) return;
          }

          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? "expanded" : "collapsed";

    const contextValue = React.useMemo<SidebarContext>(
      () => ({
        state,
        open,
        setOpen,
        primaryRailPinned,
        setPrimaryRailPinned,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        registerMobileSheet,
      }),
      [
        state,
        open,
        setOpen,
        primaryRailPinned,
        setPrimaryRailPinned,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
        registerMobileSheet,
      ],
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH,
                "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
                "--admin-primary-rail-collapsed-width":
                  ADMIN_PRIMARY_RAIL_COLLAPSED_WIDTH,
                "--admin-primary-rail-expanded-width":
                  ADMIN_PRIMARY_RAIL_EXPANDED_WIDTH,
                "--admin-secondary-menu-width": ADMIN_SECONDARY_MENU_WIDTH,
                ...style,
              } as React.CSSProperties
            }
            className={cn(
              "group/sidebar-wrapper flex min-h-svh w-full text-foreground",
              className,
            )}
            ref={ref}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  },
);
SidebarProvider.displayName = "SidebarProvider";

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
  }
>(
  (
    {
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { isMobile, state, openMobile, setOpenMobile, registerMobileSheet } =
      useSidebar();
    const [isSidebarHovered, setIsSidebarHovered] = React.useState(false);
    const rendersMobileSheet = isMobile && collapsible !== "none";
    React.useEffect(
      () => (rendersMobileSheet ? registerMobileSheet() : undefined),
      [rendersMobileSheet, registerMobileSheet],
    );

    if (collapsible === "none") {
      return (
        <div
          className={cn(
            "flex h-full w-[var(--sidebar-width)] flex-col bg-gray-1 text-foreground",
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            innerStyle={{ padding: 0 }}
            className="w-[var(--sidebar-width)] bg-gray-1 p-0 text-foreground [&>button]:hidden"
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side}
          >
            <div className="flex size-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <div
        ref={ref}
        className="group peer relative hidden md:block"
        onMouseEnter={() => setIsSidebarHovered(true)}
        onMouseLeave={() => setIsSidebarHovered(false)}
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
      >
        <SidebarHoverArrowTrigger isVisible={isSidebarHovered} />
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            "duration-200 relative h-[calc(100svh-var(--impersonation-banner-height))] w-[var(--sidebar-width)] bg-transparent transition-[width] ease-linear",
            "group-data-[collapsible=offcanvas]:w-0",
            "group-data-[side=right]:rotate-180",
            variant === "floating"
              ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
              : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]",
          )}
        />
        <div
          className={cn(
            "duration-200 fixed top-(--impersonation-banner-height) bottom-0 z-10 hidden h-[calc(100svh-var(--impersonation-banner-height))] w-[var(--sidebar-width)] transition-[left,right,width] ease-linear md:flex",
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            // Adjust the padding for floating and inset variants.
            variant === "floating"
              ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
              : variant === "inset"
                ? "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]"
                : "group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)] group-data-[side=left]:border-r group-data-[side=right]:border-l",
            className,
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex size-full flex-col overflow-hidden group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-border group-data-[variant=floating]:shadow"
          >
            {children}
          </div>
        </div>
      </div>
    );
  },
);
Sidebar.displayName = "Sidebar";

const SidebarButtonTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar, open } = useSidebar();
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            data-sidebar="trigger"
            variant="ghost"
            size="icon"
            className={cn(className)}
            onClick={(event) => {
              onClick?.(event);
              toggleSidebar();
            }}
            {...props}
          >
            <SidebarLeftOutlineIcon className="text-muted-foreground" />
            <span className="sr-only">Toggle Sidebar</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{open ? "Hide menu" : "Show menu"}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
SidebarButtonTrigger.displayName = "SidebarButtonTrigger";

const HOVER_ARROW_PATH_VARIANTS = {
  expanded: { d: "M15 6L9 19L15 32" },
  collapsed: { d: "M8.99996 6L15 19L8.99996 32" },
};

const SidebarHoverArrowTrigger = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    isVisible?: boolean;
    /** Defaults to the sidebar's own state; pass both to drive another panel. */
    collapsed?: boolean;
    onToggle?: () => void;
    buttonLabel?: string;
    buttonClassName?: string;
  }
>(
  (
    {
      isVisible,
      collapsed: collapsedProp,
      onToggle,
      buttonLabel = "Toggle sidebar",
      buttonClassName,
      className,
      ...props
    },
    ref,
  ) => {
    const { toggleSidebar, state } = useSidebar();
    const collapsed = collapsedProp ?? state === "collapsed";
    return (
      <div
        ref={ref}
        className={cn(
          "pointer-events-none absolute inset-y-0 right-0 z-20 flex items-center justify-end",
          "transition-opacity duration-150",
          isVisible ? "opacity-100" : "opacity-0",
          className,
        )}
        {...props}
      >
        <button
          type="button"
          aria-label={buttonLabel}
          onClick={onToggle ?? toggleSidebar}
          className={cn(
            "pointer-events-auto relative right-[-14px] flex h-10 w-6 cursor-pointer items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-blue-4",
            buttonClassName,
          )}
        >
          <svg
            width="24"
            height="38"
            viewBox="0 0 24 38"
            fill="none"
            className="drop-shadow-sm"
          >
            <motion.path
              variants={HOVER_ARROW_PATH_VARIANTS}
              className="stroke-gray-7 transition-colors hover:stroke-gray-10"
              initial={collapsed ? "collapsed" : "expanded"}
              animate={collapsed ? "collapsed" : "expanded"}
              transition={{ duration: 0.1 }}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    );
  },
);
SidebarHoverArrowTrigger.displayName = "SidebarHoverArrowTrigger";

const SidebarRail = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button">
>(({ className, ...props }, ref) => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      ref={ref}
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-input group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex",
        "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full group-data-[collapsible=offcanvas]:hover:bg-gray-alpha-3",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      {...props}
    />
  );
});
SidebarRail.displayName = "SidebarRail";

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"main">
>(({ className, ...props }, ref) => {
  return (
    <main
      ref={ref}
      className={cn(
        "relative flex w-full flex-1 flex-col bg-background",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl",
        className,
      )}
      {...props}
    />
  );
});
SidebarInset.displayName = "SidebarInset";

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-gray-4",
        className,
      )}
      {...props}
    />
  );
});
SidebarInput.displayName = "SidebarInput";

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn(
        "flex flex-col gap-2 p-2 group-data-[collapsible=icon]:p-2",
        className,
      )}
      {...props}
    />
  );
});
SidebarHeader.displayName = "SidebarHeader";

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  );
});
SidebarFooter.displayName = "SidebarFooter";

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-input", className)}
      {...props}
    />
  );
});
SidebarSeparator.displayName = "SidebarSeparator";

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col items-start justify-start overflow-auto hide-scrollbar group-data-[collapsible=icon]:items-center",
        className,
      )}
      {...props}
    />
  );
});
SidebarContent.displayName = "SidebarContent";

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn(
        "relative flex w-full min-w-0 flex-col px-3 py-2 group-data-[collapsible=icon]:px-2",
        className,
      )}
      {...props}
    />
  );
});
SidebarGroup.displayName = "SidebarGroup";

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const { state } = useSidebar();
  const Comp = asChild ? Slot : "div";
  if (state === "collapsed") return null;
  return (
    <Comp
      // @ts-ignore
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        textV4Variants({ variant: "fine-print-title" }),
        "flex justify-start items-center w-full p-1.5 gap-1 py-0.5 h-auto hover:bg-transparent hover:bg-gray-2 rounded text-muted-foreground mb-0.5",
        "duration-200 outline-none ring-gray-4 transition-[margin,opa] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className,
      )}
      {...props}
    />
  );
});
SidebarGroupLabel.displayName = "SidebarGroupLabel";

const SidebarGroupLabelIcon = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>((props, ref) => {
  return (
    <div ref={ref} {...props}>
      <CaretDownFillIcon className="size-2 -rotate-90 text-muted-foreground transition-transform duration-200 group-data-[state=open]/collapsible:rotate-0" />
    </div>
  );
});
SidebarGroupLabelIcon.displayName = "SidebarGroupLabelIcon";

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      // @ts-ignore
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-foreground outline-none ring-gray-4 transition-transform hover:bg-gray-2 focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
});
SidebarGroupAction.displayName = "SidebarGroupAction";

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn("w-full text-s-v4", className)}
    {...props}
  />
));
SidebarGroupContent.displayName = "SidebarGroupContent";

const SidebarMenu = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn("flex w-full min-w-0 flex-col gap-px", className)}
    {...props}
  />
));
SidebarMenu.displayName = "SidebarMenu";

const SidebarMenuItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn("group/menu-item relative list-none", className)}
    {...props}
  />
));
SidebarMenuItem.displayName = "SidebarMenuItem";

const sidebarMenuButtonVariants = cva(
  "peer/menu-button inline-flex w-full items-center justify-start overflow-hidden whitespace-nowrap rounded-lg text-left outline-none ring-gray-4 transition-[width,height,padding] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-2 focus-visible:ring-blue-4 disabled:pointer-events-none disabled:opacity-50 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:!bg-black-alpha-2 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!gap-0 group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:text-[0px] [&>span:last-child]:truncate [&>svg]:relative [&>svg]:left-[-0.5px] [&>svg]:size-3.5 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-transparent text-gray-9 hover:!bg-black-alpha-2 hover:text-foreground focus-visible:ring-blue-4 active:text-foreground disabled:pointer-events-none data-[active=true]:!bg-black-alpha-2 data-[active=true]:text-foreground data-[active=true]:font-medium-v4",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--border))] hover:!bg-black-alpha-2 hover:text-foreground hover:shadow-[0_0_0_1px_hsl(var(--muted-foreground))]",
      },
      size: {
        default: "h-7 gap-1.5 p-2",
        sm: "h-7 text-xs-v4",
        lg: "h-12 text-s-v4 group-data-[collapsible=icon]:!p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const SidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    dataTestId?: string;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = "default",
      size = "default",
      role = "button",
      tooltip,
      dataTestId,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    const button = (
      <Comp
        // @ts-ignore
        ref={ref}
        role={role}
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        data-testid={dataTestId}
        className={cn(
          sidebarMenuButtonVariants({ variant, size }),
          textV4Variants({
            variant: "compact-description",
          }),
          // `textV4Variants` adds `transition-all`; only animate layout so hover
          // background/text on menu items is instant.
          "!transition-[width,height,padding]",
          className,
        )}
        {...props}
      />
    );

    if (!tooltip) {
      return button;
    }

    if (typeof tooltip === "string") {
      tooltip = {
        children: tooltip,
      };
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={state !== "collapsed" || isMobile}
          {...tooltip}
        />
      </Tooltip>
    );
  },
);
SidebarMenuButton.displayName = "SidebarMenuButton";

const SidebarMenuAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      // @ts-ignore
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        "absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-foreground outline-none ring-gray-4 transition-transform hover:bg-muted-foreground hover:text-muted-foreground focus-visible:ring-2 peer-hover/menu-button:text-muted-foreground [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-muted-foreground md:opacity-0",
        className,
      )}
      {...props}
    />
  );
});
SidebarMenuAction.displayName = "SidebarMenuAction";

const SidebarMenuBadge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      "absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs-v4 font-medium-v4 tabular-nums text-foreground select-none pointer-events-none",
      "peer-hover/menu-button:text-muted-foreground peer-data-[active=true]/menu-button:text-muted-foreground",
      "peer-data-[size=sm]/menu-button:top-1",
      "peer-data-[size=default]/menu-button:top-1.5",
      "peer-data-[size=lg]/menu-button:top-2.5",
      "group-data-[collapsible=icon]:hidden",
      className,
    )}
    {...props}
  />
));
SidebarMenuBadge.displayName = "SidebarMenuBadge";

const SidebarMenuSkeleton = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    showIcon?: boolean;
  }
>(({ className, showIcon = false, ...props }, ref) => {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn("rounded-md h-8 flex gap-2 px-2 items-center", className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md bg-gray-3"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-[var(--skeleton-width)] flex-1 bg-gray-3"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
});
SidebarMenuSkeleton.displayName = "SidebarMenuSkeleton";

const SidebarMenuSub = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      "ml-3.5 flex min-w-0 translate-x-px flex-col gap-px border-l border-gray-3 pl-2.5 py-0.5",
      "group-data-[collapsible=icon]:hidden",
      className,
    )}
    {...props}
  />
));
SidebarMenuSub.displayName = "SidebarMenuSub";

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li"> & {
    isActive?: boolean;
    asChild?: boolean;
    dataTestId?: string;
  }
>(({ asChild = false, isActive, dataTestId, ...props }, ref) => {
  const Comp = asChild ? Slot : "li";
  return (
    <Comp
      // @ts-ignore
      ref={ref}
      data-testid={dataTestId}
      className={cn(
        textV4Variants({ variant: "compact-title" }),
        "w-full py-1 px-1.5 rounded-md text-gray-9 hover:bg-gray-2 hover:text-foreground",
        isActive && "bg-gray-2 text-foreground",
        props.className,
      )}
      {...props}
    />
  );
});
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<"a"> & {
    asChild?: boolean;
    size?: "sm" | "md";
    isActive?: boolean;
  }
>(({ asChild = false, size = "md", isActive, className, ...props }, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      // @ts-ignore
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-gray-9 outline-none ring-gray-4 hover:!bg-black-alpha-2 hover:text-foreground focus-visible:ring-2 active:!bg-black-alpha-2 active:text-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-3.5 [&>svg]:shrink-0",
        "data-[active=true]:!bg-black-alpha-2 data-[active=true]:text-foreground",
        size === "sm" && "text-xs-v4",
        size === "md" && "text-s-v4",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = "SidebarMenuSubButton";

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarHoverArrowTrigger,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarButtonTrigger,
  SidebarGroupLabelIcon,
  useSidebar,
};
